import React, { createContext, useContext, useState } from 'react';
import { AuthErrorType, AuthError, User } from '../types/auth';
import { ErrorHandler } from '../services/errorHandler';
import { useErrorNotification } from '../hooks/useErrorNotification';
import { authService } from '../services/authServices';
import { clearLegacyAuthStorage } from '../utils/authSession';

interface AuthState {
  user: User | null;
  loading: {
    login: boolean;
    logout: boolean;
    refresh: boolean;
  };
  error: AuthError | null;
  isLoading: boolean;
}

interface AuthContextType extends AuthState {
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const initialState: AuthState = {
  user: null,
  loading: { login: false, logout: false, refresh: false },
  error: null,
  isLoading: false
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(initialState);
  const { showError } = useErrorNotification();

  const login = async (identifier: string, password: string) => {
    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, login: true },
      isLoading: true,
      error: null
    }));

    try {
      const response = await authService.login(identifier, password);
      localStorage.setItem('user', JSON.stringify(response.user));
      clearLegacyAuthStorage();
      
      setState(prev => ({
        ...prev,
        user: response.user,
        loading: { ...prev.loading, login: false },
        isLoading: false,
        error: null
      }));
    } catch (error) {
      const handledError = await ErrorHandler.handleError(error);
      showError(handledError);
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, login: false },
        isLoading: false,
        error: {
          type: AuthErrorType.INVALID_CREDENTIALS,
          message: handledError.message
        }
      }));
      throw handledError;
    }
  };

  const refreshToken = async () => {
    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, refresh: true },
      isLoading: true
    }));

    try {
      await authService.refreshToken('');
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, refresh: false },
        isLoading: false,
        error: null
      }));
    } catch (error) {
      const handledError = await ErrorHandler.handleError(error);
      showError(handledError);
      setState(prev => ({
        ...prev,
        user: null,
        loading: { ...prev.loading, refresh: false },
        isLoading: false,
        error: {
          type: AuthErrorType.REFRESH_FAILED,
          message: 'Session expired. Please login again.'
        }
      }));
      throw handledError;
    }
  };

  const logout = async () => {
    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, logout: true },
      isLoading: true
    }));

    try {
      await authService.logout();
      // Clear auth tokens before state updates
      clearLegacyAuthStorage();
      localStorage.removeItem('user');
      
      // Update state after clearing storage
      setState(initialState);
      
      // Redirect to login page
      window.location.href = '/login';
    } catch (error) {
      const handledError = await ErrorHandler.handleError(error);
      showError(handledError);
      
      // Clear storage and state even on error
      clearLegacyAuthStorage();
      localStorage.removeItem('user');
      setState(initialState);
      
      // Still redirect to login
      window.location.href = '/login';
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
